class TabsCustom extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const panels = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('[role="tabpanel"]'),
    ]);
    if (panels.length === 0) return;

    const tablist = document.createElement("div");
    tablist.className = "tabs__list";
    tablist.setAttribute("role", "tablist");
    tablist.setAttribute("aria-label", this.dataset.ariaLabel || "Tabs");

    const { signal } = this.#controller;

    for (let i = 0; i < panels.length; i++) {
      const panel = panels[i];
      const heading = panel.dataset.tabHeading || `Tab ${i + 1}`;
      const panelId = panel.id;
      const tabId = panelId.replace("tabpanel-", "tab-");

      const button = document.createElement("button");
      button.type = "button";
      button.setAttribute("role", "tab");
      button.id = tabId;
      button.setAttribute("aria-selected", i === 0 ? "true" : "false");
      button.setAttribute("aria-controls", panelId);
      button.setAttribute("tabindex", i === 0 ? "0" : "-1");
      button.className = "tabs__tab";
      button.textContent = heading;

      panel.setAttribute("aria-labelledby", tabId);
      panel.hidden = i !== 0;

      button.addEventListener("click", this.#handleTabClick, { signal });
      button.addEventListener("keydown", this.#handleKeyDown, { signal });

      tablist.appendChild(button);
    }

    this.prepend(tablist);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {Event} event */
  #handleTabClick = (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    this.#activateTab(button);
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    const tablist = this.querySelector('[role="tablist"]');
    if (!tablist) return;
    const tabs = /** @type {HTMLButtonElement[]} */ ([
      ...tablist.querySelectorAll('[role="tab"]'),
    ]);
    const currentIndex = tabs.indexOf(
      /** @type {HTMLButtonElement} */ (event.currentTarget),
    );

    let newIndex = -1;

    switch (event.key) {
      case "ArrowLeft":
        newIndex = currentIndex - 1 < 0 ? tabs.length - 1 : currentIndex - 1;
        break;
      case "ArrowRight":
        newIndex = currentIndex + 1 >= tabs.length ? 0 : currentIndex + 1;
        break;
      case "Home":
        newIndex = 0;
        break;
      case "End":
        newIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    tabs[newIndex].focus();
    this.#activateTab(tabs[newIndex]);
  };

  /** @param {HTMLButtonElement} tab */
  #activateTab(tab) {
    const tablist = this.querySelector('[role="tablist"]');
    if (!tablist) return;
    const tabs = /** @type {HTMLButtonElement[]} */ ([
      ...tablist.querySelectorAll('[role="tab"]'),
    ]);
    const panels = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('[role="tabpanel"]'),
    ]);

    for (const button of tabs) {
      const isSelected = button === tab;
      button.setAttribute("aria-selected", String(isSelected));
      button.setAttribute("tabindex", isSelected ? "0" : "-1");
    }

    for (const panel of panels) {
      const isActive = panel.id === tab.getAttribute("aria-controls");
      panel.hidden = !isActive;
    }
  }
}

customElements.define("tabs-custom", TabsCustom);
